import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeContext from '../../contexts/ThemeContext';
import Icon from '../Icon';
import { FieldLabel, getScopeTypeLabel, isPreviewableAttachment } from './SummaryShared';
import TruncatedFileName from '../TruncatedFileName';
import TransferProgressBar from '../TransferProgressBar';
import i18n from '../../i18n';

const MaterialsDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ $colors }) => $colors.borderColor};
  margin: 8px 0 0 0;
  width: 100%;
`;

const MaterialsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const MaterialsHeaderLabel = styled.span`
  font-size: ${({ $layout }) => $layout.smallFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.secondaryColor};
`;

// A single shared grid with three columns: filename | preview | scope.
// (Simplified rows show only the filename.) The preview and scope columns
// auto-size to their widest content across every row, which keeps the Preview
// buttons vertically aligned even when scope values differ in width.
const FileListContainer = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  align-items: center;
  column-gap: 12px;
`;

const FileNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 10px 0;
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => $align || 'flex-start'};
  gap: 8px;
  padding: 10px 0;
`;

const RowDivider = styled.div`
  grid-column: 1 / -1;
  height: 1px;
  background: ${({ $colors }) => $colors.hoverColor};
`;

const ProgressRow = styled.div`
  grid-column: 1 / -1;
`;

const FileNameText = styled(TruncatedFileName)`
  font-size: ${({ $layout }) => $layout.mediumFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.twilight};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  text-decoration: ${({ $clickable }) => $clickable ? 'underline' : 'none'};
  text-decoration-color: transparent;
  transition: text-decoration-color 0.2s;

  ${({ $clickable }) => $clickable && `
    &:hover {
      text-decoration-color: currentColor;
    }
  `}
`;

const FileScopeText = styled.span`
  font-size: ${({ $layout }) => $layout.mediumFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.twilight};
  white-space: nowrap;
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid ${({ $colors }) => $colors.borderColor};
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  font-size: ${({ $layout }) => $layout.smallFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.twilight};
  transition: all 0.2s;

  &:hover {
    background: ${({ $colors }) => $colors.hoverColor};
    border-color: ${({ $colors }) => $colors.twilight};
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 1px solid ${({ $colors }) => $colors.borderColor};
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: ${({ $colors }) => {
      const error = $colors.errorColor || '#ea7e8f';
      return error.startsWith('#') ? `${error}1A` : 'rgba(234, 126, 143, 0.1)';
    }};
    border-color: ${({ $colors }) => $colors.errorColor};
  }
`;

const ShowAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid ${({ $colors }) => $colors.borderColor};
  background: white;
  border-radius: 20px;
  color: ${({ $colors }) => $colors.twilight};
  font-size: ${({ $layout }) => $layout.smallFontSize}px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ $colors }) => $colors.twilight};
  }
`;

const INITIAL_FILES_SHOWN = 5;

const SummaryMaterials = ({
  attachments,
  onPreview,
  onRemove,
  onDownload,
  simplified,
  progress,
  transferLabel,
  completedLabel,
}) => {
  const { colors, layout } = useContext(ThemeContext);
  const [showAllFiles, setShowAllFiles] = useState(false);

  if (attachments.length === 0) return null;

  const filesToShow = showAllFiles
    ? attachments
    : attachments.slice(0, INITIAL_FILES_SHOWN);

  const hasMoreFiles = attachments.length > INITIAL_FILES_SHOWN;

  // Three columns: filename | preview | scope. Simplified rows show only the
  // filename, so they collapse to a single column.
  const gridColumns = simplified ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto auto';

  return (
    <>
      <MaterialsDivider $colors={colors} />
      <MaterialsHeader>
        <FieldLabel>{i18n('translation_order_materials')}</FieldLabel>
        {!simplified && (
          <MaterialsHeaderLabel $colors={colors} $layout={layout}>{i18n('translation_order_estimated_scope')}</MaterialsHeaderLabel>
        )}
      </MaterialsHeader>
      <FileListContainer $columns={gridColumns}>
        {filesToShow.map((att, index) => {
          const isLast = index === filesToShow.length - 1;
          const fileProgress = progress ? progress[att.id] : undefined;
          const transferStatus = fileProgress?.status;
          // A finished transfer stays visible only when the caller has a word for it
          // ("Uploaded", TUL-5763); the detail page's download bars simply disappear.
          const showProgress = !!fileProgress && (
            transferStatus === 'active' ||
            transferStatus === 'error' ||
            (transferStatus === 'done' && !!completedLabel)
          );
          const getProgressLabel = () => {
            if (transferStatus === 'error') return i18n('transfer_failed');
            if (transferStatus === 'done') return completedLabel;
            return transferLabel;
          };
          return (
            <React.Fragment key={att.id || index}>
              <FileNameCell>
                <Icon icon="attach_file" iconLib="material" iconColor={colors.twilight} iconSize={18} withoutContainer={true} />
                <FileNameText
                  $colors={colors}
                  $layout={layout}
                  $clickable={!!onDownload}
                  onClick={onDownload ? () => onDownload(att) : undefined}
                  filename={att.filename}
                  fileSize={att.fileSize || att.size}
                  maxLength={20}
                />
              </FileNameCell>
              {!simplified && (
                <Cell>
                  {isPreviewableAttachment(att) && (
                    <PreviewButton
                      $colors={colors}
                      $layout={layout}
                      onClick={() => onPreview(att, index)}
                      aria-label={`${i18n('preview') || 'Preview'} ${att.filename}`}
                      title={i18n('preview') || 'Preview'}
                    >
                      <Icon
                        icon="visibility"
                        iconLib="material"
                        iconColor={colors.twilight}
                        iconSize={18}
                      />
                      {i18n('preview') || 'Preview'}
                    </PreviewButton>
                  )}
                </Cell>
              )}
              {!simplified && (
                <Cell $align="flex-end">
                  {att.estimatedScope && att.scopeType && (
                    <FileScopeText $colors={colors} $layout={layout}>
                      {att.estimatedScope} {getScopeTypeLabel(att.scopeType)}
                    </FileScopeText>
                  )}
                  {onRemove && (
                    <RemoveButton
                      $colors={colors}
                      onClick={() => onRemove(att, index)}
                      aria-label={`${i18n('delete') || 'Delete'} ${att.filename}`}
                      title={i18n('delete') || 'Delete'}
                    >
                      <Icon
                        icon="delete_outline"
                        iconLib="material"
                        iconColor={colors.errorColor}
                        iconSize={18}
                      />
                    </RemoveButton>
                  )}
                </Cell>
              )}
              {showProgress && (
                <ProgressRow>
                  <TransferProgressBar
                    loaded={fileProgress.loaded}
                    total={fileProgress.total}
                    status={fileProgress.status}
                    label={getProgressLabel()}
                  />
                </ProgressRow>
              )}
              {!isLast && <RowDivider $colors={colors} />}
            </React.Fragment>
          );
        })}
      </FileListContainer>

      {hasMoreFiles && !showAllFiles && (
        <ShowAllButton $colors={colors} $layout={layout} onClick={() => setShowAllFiles(true)} style={{ marginTop: 12 }}>
          {i18n('translation_order_show_all')} &#9662;
        </ShowAllButton>
      )}
    </>
  );
};

SummaryMaterials.propTypes = {
  attachments: PropTypes.array.isRequired,
  onPreview: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  onDownload: PropTypes.func,
  simplified: PropTypes.bool,
  progress: PropTypes.object,
  // Shown next to an in-flight transfer bar, e.g. "Uploading…".
  transferLabel: PropTypes.string,
  // Shown for a finished transfer, e.g. "Uploaded"; without it done entries render no bar.
  completedLabel: PropTypes.string,
};

export default SummaryMaterials;
